import React from 'react';
import { bool, node, string } from 'prop-types';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import css from './FieldRadioButton.module.css';

/**
 * A radio button from the Components board ("Yearly · save 17%"): a 20px ring with a dot in the
 * family colour when chosen, its label, and disabled. The ring turns to the error colour once the
 * group fails its `validate` after a touch or a submit, as FieldCheckbox does; the group shows the
 * message once (a FieldHelp under the radios, which they point at with `aria-describedby`).
 * `showAsRequired` draws that ring before any touch, for a form that asks for it up front. The
 * real radio stays underneath for the keyboard and screen readers.
 */
const FieldRadioButtonComponent = props => {
  const { rootClassName, className, id, label, showAsRequired, ...rest } = props;
  // Field's own config stays with Field; the rest (disabled, aria-*) goes to the input
  const {
    name,
    value,
    validate,
    format,
    parse,
    subscription,
    validateFields,
    ...inputProps
  } = rest;
  const fieldProps = { name, value, validate, format, parse, subscription, validateFields };

  return (
    <Field type="radio" {...fieldProps}>
      {({ input, meta }) => {
        const hasError = !!(meta.touched && meta.error);
        return (
          <span
            className={classNames(rootClassName || css.root, className, {
              [css.required]: showAsRequired || hasError,
            })}
          >
            <input
              id={id}
              className={css.input}
              {...inputProps}
              {...input}
              aria-invalid={hasError ? true : undefined}
            />
            <label htmlFor={id} className={css.label}>
              <span className={css.ring} aria-hidden="true">
                <span className={css.dot} />
              </span>
              <span className={css.text}>{label}</span>
            </label>
          </span>
        );
      }}
    </Field>
  );
};

FieldRadioButtonComponent.defaultProps = {
  className: null,
  rootClassName: null,
  label: null,
  showAsRequired: false,
};

FieldRadioButtonComponent.propTypes = {
  className: string,
  rootClassName: string,

  // Id is needed to connect the label with input.
  id: string.isRequired,
  label: node,

  // Name groups several RadioButtones to an array of selected values
  name: string.isRequired,

  // RadioButton needs a value that is passed forward when user checks the RadioButton
  value: string.isRequired,

  showAsRequired: bool,
};

export default FieldRadioButtonComponent;
